import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { SafeHtml } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { getApp } from "../app";
import { getDashboardData } from "../api.service";
import { NON_OM_CLIENT, RM_CLIENT } from "../customers/customers.service";
import { Subscription } from "rxjs";
import {
  DashboardDetailRequest,
  DashboardChartData,
  DashboardModel,
} from "../../../../shared/models/dashboard";
import {
  DASHBOARD_DATA_STORAGE_PREFIX,
  DashboardDataViewTransfer,
} from "./dashboard-data-view/dashboard-data-view.model";
import {
  DashboardDetailDataType,
  getDashboardDetailColumnsForChart,
} from "./dashboard-detail-columns";

interface ChartLibraryItem {
  key: string;
  title: string;
}

interface ChartLibrarySection {
  title: string;
  modifier: "rm" | "om";
  collapsed: boolean;
  items: ChartLibraryItem[];
}

const TASKS_CREATED_AND_CLOSED_CHART = "tasksCreatedAndClosedChart";

@Component({
    selector: "app-dashboard",
    templateUrl: "./dashboard.component.html",
    styleUrls: ["./dashboard.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DashboardComponent implements OnInit {
  chartsList: string[] = [];
  rmClient: boolean = false;
  nonOMClient: boolean = false;
  htmlContent: SafeHtml = "";
  time: number = 0;
  chartLibrarySections: ChartLibrarySection[] = [];
  selectedChartKeys = new Set<string>();
  dashboardModel = new DashboardModel();

  private langChangeSubscription: Subscription = new Subscription();
  private filterChangeSubscription: Subscription = new Subscription();

  app = getApp((app) => {
    this.app = app;
  });

  constructor(private router: Router) {}

  async ngOnInit() {
    this.rmClient = this.app.customers.getRMCustomers() === RM_CLIENT;
    this.nonOMClient = this.app.customers.getOMCustomers() === NON_OM_CLIENT;
    this.chartsList = this.nonOMClient
      ? this.app.list.graphs.charts
      : [
          ...this.app.list.graphs.charts,
          ...(this.app.list.graphs.charts.includes(TASKS_CREATED_AND_CLOSED_CHART)
            ? []
            : [TASKS_CREATED_AND_CLOSED_CHART]),
        ];
    this.time = Date.now();
    this.initializeSelectedCharts();
    this.refreshChartLibrary();

    await this.loadDashboardsPage();

    this.langChangeSubscription = this.app.footer.onLangChange.subscribe(
      async (value) => {
        if (value) {
          this.refreshChartLibrary();
          await this.loadDashboardsPage();
        }
      }
    );
    this.filterChangeSubscription =
      this.app.filterDashboard.onFilterChange.subscribe(async (value) => {
        if (value) {
          await this.loadDashboardsPage();
        }
      });
  }

  async loadDashboardsPage() {
    this.app.spinner.showSpinner();

    try {
      await this.getDashboardData();
    } finally {
      this.app.spinner.hideSpinner();
    }

    setTimeout(async () => {
      await this.loadSingleLineScripts();
      this.loadTextScripts();
    }, 10);
  }

  getOptionsForStatusChart() {
    /** added exception for statuses, as we don't need the status unconfirmed and ingored */
    /**https://app.asana.com/0/1209495870609871/1209855413803933 */

    let options = [];
    options = this.app.field.getOptionsAndTextsForASpecificField(
      this.app.fieldId.part.obsolescenceStatus
    );

    let unconfirmedIndex = options.findIndex(
      (item) => item.value === "unconfirmed"
    );
    if (unconfirmedIndex > -1) {
      options.splice(unconfirmedIndex, 1);
    }
    let ignoredIndex = options.findIndex((item) => item.value === "resolved");
    if (ignoredIndex > -1) {
      options.splice(ignoredIndex, 1);
    }
    return options;
  }

  async getDashboardData() {
    try {
      // Build charts based on client chartsList
      const charts: Record<string, DashboardChartData> = {};
      const allCharts: Record<string, () => DashboardChartData> =
        this.getChartsSetup();
      const selectedLibraryCharts = this.getSelectedLibraryCharts(
        Object.keys(allCharts)
      );
      let excludedCharts: string[] = [];

      if (this.app.state.isAnalyst || this.app.state.isRMAdmin) {
        excludedCharts = [
          ...this.app.list.graphs.omCharts,
          TASKS_CREATED_AND_CLOSED_CHART,
        ];
      }

      const selectedCharts = (
        selectedLibraryCharts.length > 0
          ? selectedLibraryCharts
          : this.chartsList.slice(0, 4)
      ).filter(
        (chartKey) => allCharts[chartKey] && !excludedCharts.includes(chartKey)
      );

      this.chartsList.forEach((chartKey) => {
        if (allCharts[chartKey] && !excludedCharts.includes(chartKey)) {
          (charts as any)[chartKey] = allCharts[chartKey]();
        }
      });

      // Build dashboardModel
      this.dashboardModel = {
        charts: charts,
        overview: {
          filters: this.app.filterDashboard.filters,
          selectedCharts: selectedCharts,
          labels: {
            total: this.app.text.graphs.total,
            totalUniqueCases: this.app.text.graphs.totalUniqueCases,
            currency: this.app.text.graphs.currency,
            currencySize: this.app.text.graphs.currencySize,
          },
        },
      };

      // Get data from the dashboard service
      let response = await getDashboardData(
        this.app.customers.expectCurrent,
        this.dashboardModel
      );

      // Replace text with the appropriate translation
      this.htmlContent = response
        .replace(/{{statusByMPNTitle}}/g, this.app.text.graphs.statusByMPNTitle)
        .replace(/{{statusByCPNTitle}}/g, this.app.text.graphs.statusByCPNTitle)
        .replace(/{{reachByMPNTitle}}/g, this.app.text.graphs.reachByMPNTitle)
        .replace(/{{reachByCPNTitle}}/g, this.app.text.graphs.reachByCPNTitle)
        .replace(/{{statusByOMFTitle}}/g, this.app.text.graphs.statusByOMFTitle)
        .replace(
          /{{statusOverviewTitle}}/g,
          this.app.text.graphs.statusByMonthTitle
        )
        .replace(
          /{{statusByMonthSubtitle}}/g,
          this.app.text.graphs.statusByMonthSubtitle
        )
        .replace(
          /{{tasksCreatedAndClosedTitle}}/g,
          this.app.text.graphs.tasksCreatedAndClosedTitle
        )
        .replace(/{{omApproachTitle}}/g, this.app.text.graphs.omApproachTitle)
        .replace(
          /{{selectedSolutionsTitle}}/g,
          this.app.text.graphs.selectedSolutionsTitle
        )
        .replace(
          /{{costPerFinalSolTitle}}/g,
          this.app.text.graphs.costPerFinalSolTitle
        )
        .replace(/{{contractTitle}}/g, this.app.text.graphs.contractTitle)
        .replace(
          /{{dinCodeRespDepTitle}}/g,
          this.app.text.graphs.dinCodeRespDepTitle
        )
        .replace(/{{pcnSourceTitle}}/g, this.app.text.graphs.pcnSourceTitle)
        .replace(
          /{{licenseWarningText}}/g,
          this.app.text.graphs.licenseWarningText
        )
        .replace(
          /{{noDataWarningText}}/g,
          this.app.text.graphs.noDataWarningText
        );
    } catch (error) {
      console.error("Error while loading dashboards page:", error);
    }
  }

  getChartsSetup() {
    return {
      // RM Charts
      mpnStatusChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.manufacturer.obsolescenceStatus
        ),
        hasLicense: this.rmClient,
      }),
      cpnStatusChart: () => ({
        data: this.getOptionsForStatusChart(),
        hasLicense: this.rmClient,
      }),
      mpnReachChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.manufacturer.reachAffected
        ),
        hasLicense: this.rmClient,
      }),
      cpnReachChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.manufacturer.reachAffected
        ),
        hasLicense: this.rmClient,
      }),
      // OM Charts
      omfStatusChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.thread.omfStatus
        ),
        hasLicense: !this.nonOMClient,
      }),
      openAndClosedChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.graphs.abbreviatedMonths
        ),
        hasLicense: !this.nonOMClient,
        optionalInfo: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.graphs.closedNew
        ),
      }),
      omApproachChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.thread.omApproach
        ),
        hasLicense: !this.nonOMClient,
      }),
      selectedSolutionsChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.post.resolveClass
        ),
        hasLicense: !this.nonOMClient,
      }),
      costPerFinalSolutionChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.post.resolveClass
        ),
        hasLicense: !this.nonOMClient,
      }),
      contractChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.thread.contract
        ),
        hasLicense: !this.nonOMClient,
      }),
      dinCodeRespDepChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.thread.dinCodeRespDep
        ),
        hasLicense: !this.nonOMClient,
      }),
      pcnSourceChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.thread.pcnSource
        ),
        hasLicense: !this.nonOMClient,
      }),
      tasksCreatedAndClosedChart: () => ({
        data: [],
        hasLicense: !this.nonOMClient,
        optionalInfo: [
          { text: this.app.text.graphs.openTasks, value: "open" },
          { text: this.app.text.graphs.closedTasks, value: "closed" },
        ],
      }),
    };
  }

  normalizeChartName(name: string) {
    return name.toLowerCase().replace(/[^a-z0-9]/g, "");
  }

  getChartNameMap(): Record<string, string[]> {
    return {
      mpnStatusChart: [
        "mpnStatusChart",
        "mpnStatus",
        "statusByMPNTitle",
        this.app.text.graphs.statusByMPNTitle,
      ],
      cpnStatusChart: [
        "cpnStatusChart",
        "cpnStatus",
        "statusByCPNTitle",
        this.app.text.graphs.statusByCPNTitle,
      ],
      mpnReachChart: [
        "mpnReachChart",
        "mpnReach",
        "reachByMPNTitle",
        this.app.text.graphs.reachByMPNTitle,
      ],
      cpnReachChart: [
        "cpnReachChart",
        "cpnReach",
        "reachByCPNTitle",
        this.app.text.graphs.reachByCPNTitle,
      ],
      omfStatusChart: [
        "omfStatusChart",
        "omfStatus",
        "statusByOMFTitle",
        this.app.text.graphs.statusByOMFTitle,
      ],
      openAndClosedChart: [
        "openAndClosedChart",
        "openAndClosed",
        "statusOverviewTitle",
        "statusByMonthTitle",
        this.app.text.graphs.statusByMonthTitle,
      ],
      tasksCreatedAndClosedChart: [
        "tasksCreatedAndClosedChart",
        "tasksCreatedAndClosed",
        "tasksCreatedClosed",
        this.app.text.graphs.tasksCreatedAndClosedTitle,
      ],
      omApproachChart: [
        "omApproachChart",
        "omApproach",
        "omApproachTitle",
        this.app.text.graphs.omApproachTitle,
      ],
      selectedSolutionsChart: [
        "selectedSolutionsChart",
        "selectedSolutions",
        "selectedSolutionsTitle",
        this.app.text.graphs.selectedSolutionsTitle,
      ],
      costPerFinalSolutionChart: [
        "costPerFinalSolutionChart",
        "costPerFinalSolution",
        "costPerFinalSolTitle",
        this.app.text.graphs.costPerFinalSolTitle,
      ],
      contractChart: [
        "contractChart",
        "contract",
        "contractTitle",
        this.app.text.graphs.contractTitle,
      ],
      dinCodeRespDepChart: [
        "dinCodeRespDepChart",
        "dinCodeRespDep",
        "dinCodeRespDepTitle",
        this.app.text.graphs.dinCodeRespDepTitle,
      ],
      pcnSourceChart: [
        "pcnSourceChart",
        "pcnSource",
        "pcnSourceTitle",
        this.app.text.graphs.pcnSourceTitle,
      ],
    };
  }

  initializeSelectedCharts() {
    this.selectedChartKeys = new Set(this.chartsList.slice(0, 4));
  }

  refreshChartLibrary() {
    const chartKeys = Object.keys(this.getChartsSetup());
    const availableChartKeys =
      this.chartsList.length > 0 ? this.chartsList : chartKeys;

    const sections: ChartLibrarySection[] = [
      {
        title: "RM Charts",
        modifier: "rm",
        collapsed: false,
        items: this.getChartLibraryItems(
          availableChartKeys.filter((key) => this.isRMChart(key))
        ),
      },
      {
        title: "OM Charts",
        modifier: "om",
        collapsed: false,
        items: this.getChartLibraryItems(
          availableChartKeys.filter((key) => this.isOMChart(key))
        ),
      },
    ];

    this.chartLibrarySections = sections.filter(
      (section) => section.items.length > 0
    );
  }

  getChartLibraryItems(chartKeys: string[]): ChartLibraryItem[] {
    return chartKeys.map((key) => ({
      key,
      title: this.getChartTitle(key),
    }));
  }

  getChartTitle(chartKey: string) {
    const chartNameMap = this.getChartNameMap();
    const chartNames = chartNameMap[chartKey];
    return chartNames?.[chartNames.length - 1] ?? chartKey;
  }

  isRMChart(chartKey: string) {
    return [
      "mpnStatusChart",
      "cpnStatusChart",
      "mpnReachChart",
      "cpnReachChart",
    ].includes(chartKey);
  }

  isOMChart(chartKey: string) {
    return [
      "omfStatusChart",
      "openAndClosedChart",
      TASKS_CREATED_AND_CLOSED_CHART,
      "omApproachChart",
      "selectedSolutionsChart",
      "costPerFinalSolutionChart",
      "contractChart",
      "dinCodeRespDepChart",
      "pcnSourceChart",
    ].includes(chartKey);
  }

  isChartSelected(chartKey: string) {
    return this.selectedChartKeys.has(chartKey);
  }

  toggleChartLibrarySection(section: ChartLibrarySection) {
    section.collapsed = !section.collapsed;
  }

  async toggleChartSelection(chartKey: string) {
    if (this.selectedChartKeys.has(chartKey)) {
      this.selectedChartKeys.delete(chartKey);
    } else {
      this.selectedChartKeys.add(chartKey);
    }

    await this.loadDashboardsPage();
  }

  getSelectedLibraryCharts(chartKeys: string[]) {
    return chartKeys.filter((chartKey) => this.selectedChartKeys.has(chartKey));
  }

  async loadSingleLineScripts() {
    const scripts = document.querySelectorAll("script");
    const scriptLoadPromises: Promise<void>[] = [];

    scripts.forEach((oldScript) => {
      if (oldScript.src) {
        if (document.querySelector(`script[src="${oldScript.src}"]`)) {
          const newScript = document.createElement("script");
          newScript.src = oldScript.src;
          newScript.async = false;

          const scriptPromise = new Promise<void>((resolve) => {
            newScript.onload = () => {
              resolve();
            };
          });

          scriptLoadPromises.push(scriptPromise);
          document.body.appendChild(newScript);
        }
      }
    });

    // Wait for all external scripts to load
    await Promise.all(scriptLoadPromises);
  }

  loadTextScripts() {
    const container = document.querySelector(".dashboard-container");

    if (container) {
      // Remove previously injected scripts & clear previous content
      document.querySelectorAll("script").forEach((script) => script.remove());
      container.innerHTML = "";
      container.innerHTML = this.htmlContent as string;

      const scripts = container.querySelectorAll("script");

      scripts.forEach((oldScript) => {
        if (!oldScript.src && oldScript.textContent) {
          try {
            const func = new Function(oldScript.textContent);
            func();
          } catch (e) {
            console.error("Error executing script:", e);
          }
        }
      });

      this.enhanceChartHeaders(container);
    }
  }

  enhanceChartHeaders(container: Element) {
    container
      .querySelectorAll<HTMLElement>(".card-title.mb-3.headingTop")
      .forEach((header) => {
        const title = this.getDashboardHeaderTitle(header);
        const chartKey = this.getDashboardHeaderChartKey(header);

        header.classList.add("dashboard-chart-header");
        header.closest(".card")?.classList.add("dashboard-chart-card");
        header.innerHTML = "";

        const titleElement = document.createElement("span");
        titleElement.className = "dashboard-chart-title-text";
        titleElement.textContent = title;
        header.appendChild(titleElement);

        if (chartKey != null) {
          const viewDataButton = document.createElement("button");
          viewDataButton.type = "button";
          viewDataButton.className = "dashboard-view-data-button";
          viewDataButton.innerHTML =
            '<i class="far fa-external-link"></i><span>View data</span>';
          viewDataButton.addEventListener("click", () =>
            this.openDashboardDataView(chartKey, title, header)
          );
          header.appendChild(viewDataButton);
        }
      });
  }

  openDashboardDataView(
    chartKey: string,
    title: string,
    header: HTMLElement
  ) {
    const transferId = `${chartKey}-${Date.now()}`;
    const request: DashboardDetailRequest = {
      dataType: this.getDashboardDataType(chartKey),
      dashboardModel: this.createDashboardDetailModel(chartKey),
      page: 1,
      pageSize: 25,
    };
    const canvas = header.closest(".card")?.querySelector("canvas");
    const transfer: DashboardDataViewTransfer = {
      title,
      columns: this.getDashboardDetailColumns(chartKey),
      chartImage:
        chartKey !== TASKS_CREATED_AND_CLOSED_CHART &&
        canvas instanceof HTMLCanvasElement
          ? this.createNormalizedChartImage(canvas, chartKey)
          : undefined,
      chartSummary:
        chartKey !== TASKS_CREATED_AND_CLOSED_CHART &&
        canvas instanceof HTMLCanvasElement
          ? this.createChartSummary(canvas, request.dataType, chartKey)
          : undefined,
      request,
    };

    this.clearDashboardDataViewTransfers();
    localStorage.setItem(`${DASHBOARD_DATA_STORAGE_PREFIX}${transferId}`, JSON.stringify(transfer));

    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        [
          "/",
          this.app.customers.expectCurrent,
          "dashboard",
          "data",
          chartKey,
        ],
        { queryParams: { transfer: transferId } }
      )
    );
    window.open(url, "_blank", "noopener");
  }

  createNormalizedChartImage(
    sourceCanvas: HTMLCanvasElement,
    chartKey?: string
  ) {
    const ChartConstructor = (window as any).Chart;
    const sourceChart = this.getChartInstance(sourceCanvas);

    if (!sourceChart || !ChartConstructor) {
      return sourceCanvas.toDataURL("image/png");
    }

    try {
      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = 760;
      exportCanvas.height = 360;
      const sourceOptions = sourceChart.config?.options ?? {};
      const normalizedChart = new ChartConstructor(exportCanvas, {
        type: sourceChart.config.type,
        data: JSON.parse(JSON.stringify(sourceChart.config.data)),
        plugins: sourceChart.config.plugins,
        options: {
          ...sourceOptions,
          animation: false,
          responsive: false,
          maintainAspectRatio: false,
          legend: {
            ...(sourceOptions.legend ?? {}),
            display:
              this.hasDashboardChartSummary(sourceChart, chartKey)
                ? false
                : sourceOptions.legend?.display,
            position: "bottom",
          },
          plugins: {
            ...(sourceOptions.plugins ?? {}),
            legend: {
              ...(sourceOptions.plugins?.legend ?? {}),
              display:
                this.hasDashboardChartSummary(sourceChart, chartKey)
                  ? false
                  : sourceOptions.plugins?.legend?.display,
              position: "bottom",
            },
          },
        },
      });
      const image = exportCanvas.toDataURL("image/png");
      normalizedChart.destroy();
      return image;
    } catch (error) {
      console.error("Could not normalize dashboard chart image:", error);
      return sourceCanvas.toDataURL("image/png");
    }
  }

  createChartSummary(
    sourceCanvas: HTMLCanvasElement,
    dataType: "thread" | "manufacturer" | "part" | "task",
    chartKey: string
  ) {
    const sourceChart = this.getChartInstance(sourceCanvas);
    if (!this.hasDashboardChartSummary(sourceChart, chartKey)) {
      return undefined;
    }

    if (chartKey === "openAndClosedChart") {
      const items = (sourceChart.data?.datasets ?? []).map(
        (dataset: any, index: number) => ({
          label: String(dataset.label ?? ""),
          value: (dataset.data ?? []).reduce(
            (sum: number, value: unknown) => sum + Number(value ?? 0),
            0
          ),
          color: this.getDatasetColor(dataset.backgroundColor, index),
        })
      );

      return {
        items,
        total: items.reduce(
          (sum: number, item: { value: number }) => sum + item.value,
          0
        ),
        valueLabel: "Cases",
      };
    }

    const labels = sourceChart.data?.labels ?? [];
    const dataset = sourceChart.data?.datasets?.[0];
    const values = dataset?.data ?? [];
    const colors = Array.isArray(dataset?.backgroundColor)
      ? dataset.backgroundColor
      : [];
    const items = labels.map((label: unknown, index: number) => ({
      label: String(label ?? ""),
      value: Number(values[index] ?? 0),
      color: String(colors[index] ?? "#a0aec0"),
    }));

    return {
      items,
      total: items.reduce((sum: number, item: { value: number }) => sum + item.value, 0),
      valueLabel:
        dataType === "manufacturer"
          ? "Manufacturers"
          : dataType === "part"
            ? "Parts"
            : dataType === "task"
              ? "Tasks"
            : "Cases",
    };
  }

  hasDashboardChartSummary(sourceChart: any, chartKey?: string) {
    return (
      sourceChart?.config?.type === "doughnut" ||
      chartKey === "openAndClosedChart"
    );
  }

  getDatasetColor(backgroundColor: unknown, index: number) {
    if (Array.isArray(backgroundColor)) {
      return String(backgroundColor[index] ?? backgroundColor[0] ?? "#a0aec0");
    }
    return String(backgroundColor ?? "#a0aec0");
  }

  getChartInstance(sourceCanvas: HTMLCanvasElement): any {
    const ChartConstructor = (window as any).Chart;
    const legacyChart = Object.values(ChartConstructor?.instances ?? {}).find(
      (instance: any) => instance?.canvas === sourceCanvas
    );
    return ChartConstructor?.getChart?.(sourceCanvas) ?? legacyChart;
  }

  getDashboardDataType(chartKey: string): DashboardDetailDataType {
    if (chartKey === TASKS_CREATED_AND_CLOSED_CHART) {
      return "task";
    }
    if (chartKey === "mpnStatusChart" || chartKey === "mpnReachChart") {
      return "manufacturer";
    }
    if (chartKey === "cpnStatusChart" || chartKey === "cpnReachChart") {
      return "part";
    }
    return "thread";
  }

  createDashboardDetailModel(chartKey: string): DashboardModel {
    const allCharts: Record<string, () => DashboardChartData> = this.getChartsSetup();

    return {
      charts: {
        [chartKey]: allCharts[chartKey](),
      },
      overview: {
        filters: this.app.filterDashboard.filters,
        selectedCharts: [chartKey],
        labels: {
          total: this.app.text.graphs.total,
          totalUniqueCases: this.app.text.graphs.totalUniqueCases,
          currency: this.app.text.graphs.currency,
          currencySize: this.app.text.graphs.currencySize,
        },
      },
    };
  }

  clearDashboardDataViewTransfers() {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(DASHBOARD_DATA_STORAGE_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  }

  getDashboardDetailColumns(chartKey: string) {
    return getDashboardDetailColumnsForChart(
      chartKey,
      this.getDashboardDataType(chartKey)
    );
  }

  getDashboardHeaderTitle(header: HTMLElement) {
    const chartKey = this.getDashboardHeaderChartKey(header);

    if (chartKey != null) {
      const prefix = this.isRMChart(chartKey) ? "RM" : "OM";
      return `${prefix} • ${this.getChartTitle(chartKey)}`;
    }

    return header.textContent?.trim() ?? "";
  }

  getDashboardHeaderChartKey(header: HTMLElement) {
    const candidates: string[] = [];

    header
      .querySelectorAll<HTMLOptionElement>("select option:checked")
      .forEach((option) => {
        candidates.push(
          option.value,
          option.id,
          option.textContent?.trim() ?? "",
          option.getAttribute("data-chart-key") ?? ""
        );
      });

    header
      .querySelectorAll<HTMLElement>(".dropdown-toggle, .dropdown-item")
      .forEach((element) => {
        candidates.push(
          element.dataset.chartKey ?? "",
          element.id,
          element.textContent?.trim() ?? ""
        );
      });

    candidates.push(header.textContent?.trim() ?? "");

    return this.getChartKeyFromCandidates(candidates);
  }

  getChartKeyFromCandidates(candidates: string[]) {
    const chartNameMap = this.getChartNameMap();
    const chartResponseKeyMap: Record<string, string> = {
      statusByMPN: "mpnStatusChart",
      statusByCPN: "cpnStatusChart",
      reachByMPN: "mpnReachChart",
      reachByCPN: "cpnReachChart",
      statusByOMF: "omfStatusChart",
      statusByOpenClosed: "openAndClosedChart",
      tasksCreatedAndClosed: TASKS_CREATED_AND_CLOSED_CHART,
      statusByOMApproach: "omApproachChart",
      selectedSolutions: "selectedSolutionsChart",
      costPerFinalSolution: "costPerFinalSolutionChart",
      statusByContract: "contractChart",
      responsibleDepartment: "dinCodeRespDepChart",
      pcnSource: "pcnSourceChart",
    };

    for (const candidate of candidates) {
      if (chartResponseKeyMap[candidate] != null) {
        return chartResponseKeyMap[candidate];
      }

      const normalizedCandidate = this.normalizeChartName(candidate);

      if (normalizedCandidate === "") {
        continue;
      }

      const chartKey = Object.keys(chartNameMap).find((key) => {
        const chartNames = chartNameMap[key] ?? [key];
        return chartNames.some((chartName) => {
          const normalizedTitle = this.normalizeChartName(chartName);
          return (
            normalizedTitle !== "" &&
            (normalizedCandidate === normalizedTitle ||
              normalizedCandidate.includes(normalizedTitle) ||
              normalizedTitle.includes(normalizedCandidate))
          );
        });
      });

      if (chartKey != null) {
        return chartKey;
      }
    }

    return null;
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
    if (this.filterChangeSubscription) {
      this.filterChangeSubscription.unsubscribe();
    }

    this.app.filterDashboard.isOpen = false;
    this.app.filterDashboard.resetFilter(false);
  }
}
